// stores/organizationsStore.ts
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist } from "zustand/middleware";
import { OrganizationWithoutUsers, OrganizationWithUsers } from "@/lib/types";
import { api } from "@/lib/api";

interface OrganizationsState {
  organizations: OrganizationWithUsers[];
  selectedOrganizationId: string | null;
  setOrganizations: (organizations: OrganizationWithUsers[]) => void;
  addOrganization: (organization: OrganizationWithUsers) => void;
  updateOrganization: (
    id: string,
    updatedOrganization: Partial<OrganizationWithUsers>
  ) => void;
  deleteOrganization: (id: string) => void;
  setSelectedOrganizationId: (id: string | null) => void;
  fetchOrganizations: (
    organizationsFromUserObject: OrganizationWithoutUsers[] | undefined
  ) => Promise<void>;
}

export const useOrganizationsStore = create<OrganizationsState>()(
  persist(
    immer((set, get) => ({
      organizations: [],
      selectedOrganizationId: null,
      setOrganizations: (organizations) =>
        set((state) => {
          state.organizations = organizations;
        }),
      addOrganization: (organization) =>
        set((state) => {
          state.organizations.push(organization);
        }),
      updateOrganization: (id, updatedOrganization) =>
        set((state) => {
          const index = state.organizations.findIndex((org) => org.id === id);
          if (index !== -1) {
            Object.assign(state.organizations[index], updatedOrganization);
          }
        }),
      deleteOrganization: (id) =>
        set((state) => {
          state.organizations = state.organizations.filter(
            (org) => org.id !== id
          );
        }),
      setSelectedOrganizationId: (id) =>
        set((state) => {
          state.selectedOrganizationId = id;
        }),
      fetchOrganizations: async (organizationsFromUserObject) => {
        try {
          const organizations = await api.listOrganizations();
          set((state) => {
            state.organizations = organizations;
            if (organizationsFromUserObject) {
              const mergedOrgs = [...organizations];
              organizationsFromUserObject.forEach((userOrg) => {
                if (!mergedOrgs.some((org) => org.id === userOrg.id)) {
                  mergedOrgs.push(userOrg as OrganizationWithUsers);
                }
              });
              state.organizations = mergedOrgs;
            }
          });
        } catch (error) {
          console.error("Failed to fetch organizations:", error);
          if (organizationsFromUserObject) {
            set((state) => {
              state.organizations =
                organizationsFromUserObject as OrganizationWithUsers[];
            });
          }
        }
      },
    })),
    {
      name: "organization-storage",
      partialize: (state) => ({
        selectedOrganizationId: state.selectedOrganizationId,
      }),
    }
  )
);
